import { Divider, Stack, Typography } from '@mui/material'
import { AppV1Appsowner } from 'c1gen/roles'
import { C1EmptyIndicator } from 'components/core/empty-indicator/C1EmptyIndicator'
import { C1MultiUserDisplayWithFetch } from 'components/core/multi-user/C1MultiUserDisplay'
import { UserDirectoryStatus } from 'components/core/user'
import { useHasRole } from 'components/no-ui/capability'
import { removeDoubleQuotes } from 'components/no-ui/string-formatting'
import { UserEmploymentStatus } from 'components/pages/profile'
import { UserEmploymentType } from 'components/pages/user/details/UserDetail'
import { UserDisplayName } from 'components/pages/user/user-display-name'
import { TaskValue } from 'lib/actions/task'
import { SearchRolesRequest } from 'pbts/c1/api/iam/v1/iam'
import { Value } from 'pbts/google/protobuf/struct'
import { FC, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useGetAllRoles } from 'redux_toolkit/roles'
import { TaskKeyValueMetadataRow } from './TaskKeyValueMetadataRow'

export const TaskUserInfo: FC<{ task: TaskValue }> = ({ task }) => {
    const intl = useIntl()
    const profile = task.user?.profile
    const idUser = task.identityUser || task.user
    const isAppOwner = useHasRole(AppV1Appsowner)
    const extraUsernames = task.user?.usernames?.filter((e) => e !== task.user?.username) || []

    const { data: allRoles } = useGetAllRoles(SearchRolesRequest.create({}))

    // Helper function to convert role IDs to display names
    const roleDisplayNames = useMemo(() => {
        if (!allRoles || !task.user?.roleIds || task.user.roleIds.length === 0) return ''

        const flattenedRoles = allRoles.flat()
        const displayNames = task.user.roleIds.map((roleId) => {
            const role = flattenedRoles.find((r) => r.id === roleId)
            return role?.displayName || roleId // fallback to ID if not found
        }).filter(Boolean)

        return displayNames.join(', ')
    }, [allRoles, task.user?.roleIds])

    return <Stack mt={3} spacing={5}>
        <Stack spacing={2}>
            <Typography variant="h4">
                <FormattedMessage id="user_details_task_title" defaultMessage="User details" />
            </Typography>
            <Divider sx={{ my: 4 }} />
            <Stack spacing={3} pt={2}>
                <TaskKeyValueMetadataRow
                    label={intl.formatMessage({ id: 'user_details_name', defaultMessage: 'Name' })}
                    value={idUser ? <UserDisplayName
                        user={idUser}
                        readOnly={!isAppOwner}
                        margin={false}
                        includeDetails={false}
                        showTooltip
                    /> : task.user?.displayName || <C1EmptyIndicator />}
                />

                {task.user?.email && <TaskKeyValueMetadataRow
                    label={<FormattedMessage id="components_task_details_content_tab_details_email" defaultMessage="Email" />}
                    value={task.user?.email || <C1EmptyIndicator />}
                />}
                {task.user?.jobTitle && <TaskKeyValueMetadataRow
                    label={<FormattedMessage id="components_task_details_content_tab_details_job_title" defaultMessage="Job title" />}
                    value={task.user?.jobTitle || <C1EmptyIndicator />}
                />}
                {task.user?.department && <TaskKeyValueMetadataRow
                    label={<FormattedMessage id="components_task_details_content_tab_details_department" defaultMessage="Department" />}
                    value={task.user?.department || <C1EmptyIndicator />}
                />}
                {task.user?.managerIds && task.user?.managerIds.length > 0 && <TaskKeyValueMetadataRow
                    label={<FormattedMessage id="components_task_details_content_tab_details_managers" defaultMessage="Managers" />}
                    value={<C1MultiUserDisplayWithFetch userIds={task.user?.managerIds || []} readOnly margin={false} />}
                />}
                {task.user?.roleIds && task.user?.roleIds.length > 0 && <TaskKeyValueMetadataRow
                    label={<FormattedMessage id="user_details_role" defaultMessage="Role" />}
                    value={roleDisplayNames || <C1EmptyIndicator />}
                />}
                {extraUsernames && extraUsernames.length > 0 && <TaskKeyValueMetadataRow
                    label={<FormattedMessage id="user_details_usernames" defaultMessage="Usernames" />}
                    value={extraUsernames?.join(', ') || <C1EmptyIndicator />}
                />}
                {task.user?.employeeIds && task.user?.employeeIds.length > 0 && <TaskKeyValueMetadataRow
                    label={<FormattedMessage id="user_details_employee_id" defaultMessage="Employee ID" />}
                    value={task.user?.employeeIds?.join(', ') || <C1EmptyIndicator />}
                />}
                {task.user?.employmentStatusSources && task.user?.employmentStatusSources.length > 0 &&
                    task.user?.employmentStatus &&
                    <TaskKeyValueMetadataRow
                        label={<FormattedMessage id="user_details_employment_status" defaultMessage="Employment status" />}
                        value={<UserEmploymentStatus user={task.user} intl={intl} />}
                    />}
                {task.user?.employmentTypeSources &&
                    task.user?.employmentTypeSources.length > 0
                    && task.user?.employmentType &&
                    <TaskKeyValueMetadataRow
                        label={<FormattedMessage id="user_details_employment_type" defaultMessage="Employment type" />}
                        value={<UserEmploymentType user={task.user} intl={intl} />}
                    />}
                {!!task.user?.directoryStatus && <TaskKeyValueMetadataRow
                    label={<FormattedMessage id="user_details_directory_status" defaultMessage="Directory status" />}
                    value={<UserDirectoryStatus user={task.user} intl={intl} />}
                />}
            </Stack>
        </Stack>
        <Stack spacing={2} pt={2}>
            <Typography variant="h4">
                <FormattedMessage id="user_details_profile_attributes" defaultMessage="Profile attributes" />
            </Typography>
            <Divider sx={{ my: 4 }} />
            <Stack spacing={3} pt={2}>
                {profile && Object.entries(profile.fields).length > 0 ? Object.entries(profile.fields).map(([key, value]) => (
                    <TaskKeyValueMetadataRow
                        key={key}
                        label={key}
                        value={removeDoubleQuotes(Value.toJsonString(value, { prettySpaces: 2 }))}
                    />
                )) : <Typography variant="unconfigured"><FormattedMessage id="user_details_profile_empty" defaultMessage="None" /></Typography>}
            </Stack>
        </Stack>
    </Stack>
}
